import Papa from 'papaparse';
import _ from 'lodash';
import Event from './Event';
import Action from './Action';
import ScenarioList from './ScenarioList';
import Locale from './Locale';

const numRowsInScenarioDef = 3;
const numRowsInEventDef = 9;
const numColumnsInActionDef = 8;
const eventStride = 4;

class Scenario {
  static loadScenario(name) {
    let scenarioIndex = _(ScenarioList).map('path').indexOf(name);
    let scenarioInfo = ScenarioList[scenarioIndex];
    let results = Papa.parse(scenarioInfo.data[Locale.locale()]);

    if (results.errors.length > 0) {
      return({errors: results.errors});
    } else {
      var scenario = null;
      var errors = [];
      try {
        scenario = new Scenario(results.data);
        scenario.name = scenarioInfo.name;
        scenario.path = scenarioInfo.path;
        scenario.startingScore = scenarioInfo.startingScore;
        scenario.level = scenarioIndex;
        errors = scenario.validate();
      } catch (e) {
        errors.push(e);
      }
      return({scenario: scenario, errors: errors});
    }
  };

  static loadScenarioFromFile(file, completion) {
    Papa.parse(file, {
      download: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          completion(null, results.errors);
        } else {
          var scenario = null;
          var errors = [];
          try {
            scenario = new Scenario(results.data);
            errors = scenario.validate();
          } catch (e) {
            errors.push(e);
          }
          completion(scenario, errors);
        }
      }
    })
  };

  constructor(data) {
    this.name = 'Unknown';
    this.path = 'city';
    this.description = data[1][2];
    this.events = this.buildEvents(data.slice(numRowsInScenarioDef, numRowsInScenarioDef + numRowsInEventDef).map((row) => { return row.slice(numColumnsInActionDef + 1) })); // Trim the non-data columns
    this.wildcards = this.buildWildcards(data.slice(numRowsInScenarioDef, numRowsInScenarioDef + numRowsInEventDef).map((row) => { return row.slice(numColumnsInActionDef + 1) })); // Trim the non-data columns
    this.actions = this.buildActions(data.slice(numRowsInScenarioDef + numRowsInEventDef + 1)); // Note that we add one row for the column headers
  }

  buildEvents(data) {
    // Receives 'eventStride' columns, with each column being, in order ((hazard, isWildcard, hazard description, event description, hint, gameover message, iconFilename, photoFilename, $ impact), (_, _, _, _, _, _, _, _, p impact), (_, _, _, _, _, _, _, _, q impact), (_, _, _, _, _, _, _, _, _))
    var events = []
    for (var i = 0; (i*eventStride) < data[0].length; i++) {
      let baseIndex = i*eventStride;
      if (data[1][baseIndex].length === 0) {
        events.push(new Event(i, false, data[0][baseIndex], data[2][baseIndex], data[3][baseIndex], data[4][baseIndex], data[5][baseIndex], data[6][baseIndex], data[7][baseIndex], data[8][baseIndex], data[8][baseIndex+1], data[8][baseIndex+2]));
      }
    }
    return events;
  }

  buildWildcards(data) {
    // Receives 'eventStride' columns, with each column being, in order ((hazard, isWildcard, hazard description, event description, hint, gameover message, iconFilename, photoFilename, $ impact), (_, _, _, _, _, _, _, _, p impact), (_, _, _, _, _, _, _, _, q impact), (_, _, _, _, _, _, _, _, _))
    var wildcards = []
    for (var i = 0; (i*eventStride) < data[0].length; i++) {
      let baseIndex = i*eventStride;
      if (data[1][baseIndex].length > 0) {
        wildcards.push(new Event(i, true, data[0][baseIndex], data[2][baseIndex], data[3][baseIndex], data[4][baseIndex], data[5][baseIndex], data[6][baseIndex], data[7][baseIndex], data[8][baseIndex], data[8][baseIndex+1], data[8][baseIndex+2]));
      }
    }
    return wildcards;
  }

  buildActions(data) {
    // Receives n rows, one for each action
    // Each row is (description, risk, category, rationale, iconFilename, c_cost, p_cost, q_cost, _, [impacts...])
    // Each impact entry is a 4-tuple (c_benefit, p_benefit, q_benefit, rationale)
    return data.map( (row) => {
      // Note that we skip column 8
      return new Action(row[0], row[1], row[2], row[3], row[4], row[5], row[6], row[7], row.slice(9));
    });
  }

  validate() {
    var errors = _([]);
    if (!this.name.length > 0) errors.push("Scenario name is empty");
    if (!this.path.length > 0) errors.push("Scenario path is empty");
    if (!this.description.length > 0) errors.push("Scenario description is empty");

    // Note that the passed in row and event indexes here are 1 indexed
    errors = errors.concat(this.actions.map((action, i) => { return action.validate(i + numRowsInScenarioDef + numRowsInEventDef + 2); }));
    errors = errors.concat(this.events.map((event, i) => { return event.validate(i + 1); }));
    errors = errors.concat(this.wildcards.map((wildcard, i) => { return wildcard.validate(i + 1); }));

    return errors
      .flatten()
      .compact()
      .value();
  }


  actionCategories(cost, excluding) {
    return this.actionsSatisfying(cost, excluding)
      .map('category')
      .uniq()
      .sort()
      .value();
  }

  randomActionsForCategory(category, cost, excluding, count) {
    return this.actionsSatisfying(cost, excluding)
      .filter((action) => { return action.category === category })
      .sampleSize(count)
      .value();
  }

  actionsSatisfying(cost, excluding) {
    let wrappedExcluding = _(excluding);
    return _(this.actions)
      .filter((action) => { return -action.cost.c < cost })
      .filter((action) => { return !wrappedExcluding.includes(action) });
  }

  eventsByHazard() {
    return _(this.events)
      .uniqBy('hazard')
      .value();
  }

  randomEvent() {
    return _(this.events).sample();
  }

  randomWildcard() {
    return _(this.wildcards).sample();
  }
}

export default Scenario;



// WEBPACK FOOTER //
// ./src/models/Scenario.js